import { WarmupPoolTier, WarmupLanguage, WarmupLevel } from './enums';

/**
 * Microsoft identity platform v2.0 authority root, used to build both the
 * tenant client-credentials token endpoint and the admin-consent URL. Shared
 * with `@trulyinbox/dwd-auth` as the fallback when `MICROSOFT_AUTHORITY_URL`
 * is unset in config.
 */
export const MICROSOFT_AUTHORITY_URL = 'https://login.microsoftonline.com';

export const RECEIVING_QUOTA_RATIO = 1.5;
export const WARMUP_TASK_TIMEOUT_MS = 10_000;
export const WARMUP_LOCK_TTL_MS = 3_600_000;
export const TOKEN_CACHE_TTL_SECONDS = 300;
export const TOKEN_REFRESH_LOCK_TTL_MS = 30_000;
export const DELIVERY_GATE_CHECK_DELAY_MS = 7_200_000;
export const POD_STARTUP_BATCH_SIZE = 100;
export const CONTENT_POOL_MIN_THRESHOLD = 50;
export const RAMP_MIN_SAMPLE_SIZE = 10;
export const MESSAGE_ID_DOMAIN = 'trulyinbox.com';
export const SPAM_RATE_WARN_THRESHOLD = 10.0;
export const SPAM_RATE_DANGER_THRESHOLD = 15.0;
export const SPAM_RATE_PAUSE_THRESHOLD = 30.0;
export const BOUNCE_RATE_WARN_THRESHOLD = 3.0;
export const BOUNCE_RATE_DANGER_THRESHOLD = 5.0;
// Pool tier floors (Spec §2.3): Pool A ≥ 85, Pool B 60–84, Pool C < 60.
export const POOL_PROMOTION_B_DAYS = 5;
export const POOL_PROMOTION_A_DAYS = 10;

// ── Composite pool score (Spec §2.3) ────────────────────────────────────────
//
// Per-signal weights for the weighted composite pool score (0–100). Sum to 100.
// The composite renormalizes over only the signals with live data this pass:
// `lastReceived` is always dropped (no MAX(receivedAt) pipeline yet) and
// `spamFolder` drops when spam is unmeasurable (MySQL-fallback NaN sentinel).
// See `pool-score.service.ts`.
export const POOL_SIGNAL_WEIGHTS = {
  deliverability: 40,
  connection: 20,
  domainBlacklist: 15,
  bounce: 10,
  spamFolder: 8,
  age: 4,
  lastReceived: 3,
} as const;

/** Network tenure (days since account creation) at which the age sub-score saturates to 100. */
export const POOL_AGE_FULL_SCORE_DAYS = 30;
/** bounceRate (%) at/above which the bounce sub-score hits 0 (= BOUNCE_RATE_DANGER_THRESHOLD). */
export const POOL_BOUNCE_ZERO_SCORE_RATE = 5;
/** spamRate (%) at/above which the spam-folder sub-score hits 0 (= SPAM_RATE_DANGER_THRESHOLD). */
export const POOL_SPAM_ZERO_SCORE_RATE = 15;

// Composite-score tier thresholds (reuse the prior numeric floors, now keyed off
// the composite instead of raw deliverability). Pool A ≥ 85, Pool B 60–84, C < 60.
export const POOL_A_MIN_SCORE = 85;
export const POOL_B_MIN_SCORE = 60;
/** C→B promotion: composite ≥ this for POOL_PROMOTION_B_DAYS consecutive qualifying days. */
export const POOL_PROMOTION_B_SCORE = 70;
/** B→A promotion: composite ≥ this for POOL_PROMOTION_A_DAYS consecutive qualifying days. */
export const POOL_PROMOTION_A_SCORE = 85;

// Bulk interaction size bounds (plan §3): each BULK_READ / BULK_FLAG /
// BULK_MOVE_TO_INBOX op touches a random 3–10 received emails. Shared by the
// bulk executor (Phase 2) and the planner's bulk injection (Phase 3).
export const WARMUP_BULK_MIN_SIZE = 3;
export const WARMUP_BULK_MAX_SIZE = 10;

// Warmup conversation threads (plan: warmup-thread-tendency). A thread is hard-
// capped at 4 emails (`threadDepth`: send = 1, each reply = parent + 1); the
// planner excludes depth-4 rows from continuation. The per-hop continue
// probability comes from `threadBandForLimit` (the account's current daily
// limit), so there is no fallback constant.
export const WARMUP_THREAD_MAX_EMAILS = 4;

// `warmupEmailHistory.id` floor for the MOVE-type interactions only
// (SPAM_TO_INBOX, RECATEGORIZE, BULK_MOVE_TO_INBOX). Rows below it predate mono
// warming the mailbox; legacy may already have moved those messages, so a move
// on one is a guaranteed not-found. OPEN/FLAG/REPLY/FORWARD/BULK_READ/BULK_FLAG
// still act on them — the message is still in the mailbox (2026-10-13: 650 OPENs
// on legacy rows succeeded in 12h).
// ponytail: fixed id cutoff, swap for a per-mailbox connected-at bound if a later migration wave needs it
export const MIN_CANDIDATE_HISTORY_ID = 450_000_000;

export const RAMP_THRESHOLDS = {
  EXCELLENT: 0.99,
  STRONG: 0.97,
  GOOD: 0.95,
} as const;

/**
 * Fixed daily-send floor for every mailbox: where warmup starts, where a
 * throttle exits to, and the lowest `warmupMaxLimit` a user may set. Not
 * plan-dependent and not user-editable.
 */
export const WARMUP_MIN_LIMIT = 5;

// ── Reply-rate & thread-tendency bands, keyed by daily limit ────────────────
//
// `WarmupLevel` is an INTERNAL band key here, never a user pick and never a
// persisted source of truth: it is derived at read time from a mailbox's
// current daily limit (`warmupCurrentLimit`) via `warmupLevelFromVolume`, so the
// bands follow the limit both up and down. The planner draws a fresh uniform
// value inside the band each tick. A custom reply rate (`replyRateCustom`)
// replaces the reply band with the user's own stored value; thread tendency has
// no custom override. Resolve through `replyBandForLimit` / `threadBandForLimit`
// rather than indexing these Records directly.
export const WARMUP_LEVEL_REPLY_RATE: Record<WarmupLevel, { min: number; max: number }> = {
  [WarmupLevel.NOVICE]: { min: 10, max: 15 },
  [WarmupLevel.APPRENTICE]: { min: 10, max: 20 },
  [WarmupLevel.EXPERT]: { min: 18, max: 28 },
  [WarmupLevel.MASTER]: { min: 28, max: 45 },
  [WarmupLevel.GRANDMASTER]: { min: 42, max: 50 },
};

export const WARMUP_LEVEL_THREAD_TENDENCY: Record<WarmupLevel, { min: number; max: number }> = {
  [WarmupLevel.NOVICE]: { min: 5, max: 10 },
  [WarmupLevel.APPRENTICE]: { min: 10, max: 20 },
  [WarmupLevel.EXPERT]: { min: 18, max: 30 },
  [WarmupLevel.MASTER]: { min: 20, max: 30 },
  [WarmupLevel.GRANDMASTER]: { min: 20, max: 35 },
};

/**
 * Absolute ceiling (%) for any reply rate, custom or auto — the hard bound the
 * settings DTO's `@Max` enforces and the last clamp every plan cap passes
 * through. It is NOT the only bound: the org's per-plan `replyRateCap`
 * restriction (seeded per tier — free 10, trial 45, starter 25, growth 45,
 * scale 60, scale-plus 65) sits under it and is the effective ceiling on both
 * a custom value and the top (40+/day) auto band. See
 * `resolveWarmupLevelReplyBand`.
 */
export const REPLY_RATE_ABSOLUTE_CAP = 65;

/** Band keys in ascending daily-limit order (<10, 10–19, 20–29, 30–39, 40+). */
export const WARMUP_LEVEL_ORDER: WarmupLevel[] = [
  WarmupLevel.NOVICE,
  WarmupLevel.APPRENTICE,
  WarmupLevel.EXPERT,
  WarmupLevel.MASTER,
  WarmupLevel.GRANDMASTER,
];

/**
 * The top band (a daily limit of 40+) — the ONE band whose reply ceiling comes
 * from the org's plan (`replyRateCap`) rather than the preset above. Every
 * lower band keeps its fixed reply range; only this one stretches to whatever
 * the customer is paying for. Derived from the order array so it can never
 * drift from it.
 */
export const PLAN_MAX_WARMUP_LEVEL: WarmupLevel =
  WARMUP_LEVEL_ORDER[WARMUP_LEVEL_ORDER.length - 1];

/** One `emailAccountWarmupSchedule` row, minus its ids. */
export interface WarmupScheduleSlot {
  /** 0 = Monday … 6 = Sunday. */
  dayOfWeek: number;
  slotIndex: number;
  weight: number;
  enabled: boolean;
  /** `HH:MM:SS`, in the mailbox's own timezone. */
  startTime: string;
  endTime: string;
}

/**
 * The default sending schedule: one 09:00–17:00 slot per day, Mon–Fri on and
 * Sat–Sun off. It's what enabling warmup seeds, what the settings screen shows
 * for a day with no stored rows, and what an org that loses
 * `warmup-setting/sending-schedule` is reset to.
 *
 * An account with NO rows is not "on the default" — the planner reads zero
 * enabled rows as a day off, every day. Resetting a schedule means writing
 * these rows, never deleting the old ones and stopping there.
 */
export const DEFAULT_WARMUP_SCHEDULE: ReadonlyArray<Readonly<WarmupScheduleSlot>> = [
  0, 1, 2, 3, 4, 5, 6,
].map((dayOfWeek) => ({
  dayOfWeek,
  slotIndex: 0,
  weight: 100,
  enabled: dayOfWeek < 5,
  startTime: '09:00:00',
  endTime: '17:00:00',
}));

export const ELIGIBLE_RECIPIENT_POOLS: Record<WarmupPoolTier, WarmupPoolTier[]> = {
  [WarmupPoolTier.A]: [WarmupPoolTier.A, WarmupPoolTier.B, WarmupPoolTier.C],
  [WarmupPoolTier.B]: [WarmupPoolTier.B, WarmupPoolTier.C],
  [WarmupPoolTier.C]: [WarmupPoolTier.C],
};

/**
 * Fallback recipient name used when a recipient's first name is unknown and
 * the warmup language has no language-specific generic name. English default.
 */
export const RECIPIENT_FALLBACK_NAME = 'Friend';

/**
 * Language-specific generic recipient names, substituted for `{first name}`
 * tokens when the real recipient name is unavailable. English is intentionally
 * omitted — it falls back to `RECIPIENT_FALLBACK_NAME`.
 */
export const LANGUAGE_GENERIC_NAMES: Partial<Record<WarmupLanguage, string>> = {
  [WarmupLanguage.Spanish]: 'Amigo',
  [WarmupLanguage.French]: 'Ami',
  [WarmupLanguage.German]: 'Freund',
  [WarmupLanguage.Portuguese]: 'Amigo',
  [WarmupLanguage.Chinese]: '朋友',
  [WarmupLanguage.Japanese]: '友達',
  [WarmupLanguage.Arabic]: 'صديق',
  [WarmupLanguage.Hindi]: 'दोस्त',
  [WarmupLanguage.Russian]: 'Друг',
  [WarmupLanguage.Italian]: 'Amico',
  [WarmupLanguage.Dutch]: 'Vriend',
};

// ── Warmup content: target industry + subtopic catalogs ─────────────────────
//
// Canonical predefined lists for the `industry_topic` content mode. Shared
// across the frontend (the Industry / Topics dropdowns in the warmup settings
// form) and the backend (`@IsIn` validation on `UpdateWarmupSettingsDto`), so
// FE choices and BE acceptance never drift. The stored/persisted value is the
// `value` string (what the AI content pipeline reads) — `key` is a stable slug
// for analytics/future i18n. Keep both lists append-only; reordering is fine
// but renaming a `value` orphans existing accounts that stored the old string.

export type LabeledOption = { key: string; value: string };

export const TARGET_INDUSTRY_OPTIONS: readonly LabeledOption[] = [
  { key: 'saas', value: 'SaaS' },
  { key: 'ecommerce_retail', value: 'Ecommerce/Retail' },
  { key: 'b2b_services', value: 'B2B Services' },
  { key: 'marketing_advertising', value: 'Marketing/Advertising' },
  { key: 'real_estate', value: 'Real Estate' },
  { key: 'finance_fintech', value: 'Finance/Fintech' },
  { key: 'education_edtech', value: 'Education/Edtech' },
  { key: 'healthcare', value: 'Healthcare' },
  { key: 'recruitment_hr', value: 'Recruitment/HR' },
  { key: 'hospitality_travel', value: 'Hospitality/Travel' },
  { key: 'legal_consulting', value: 'Legal/Consulting' },
  { key: 'it_technology', value: 'IT/Technology' },
  { key: 'professional_services', value: 'Professional Services' },
  { key: 'nonprofit_ngo', value: 'Nonprofit/NGO' },
  { key: 'media_publishing', value: 'Media/Publishing' },
] as const;

export const SUBTOPIC_OPTIONS: readonly LabeledOption[] = [
  { key: 'process_optimization', value: 'Process Optimization' },
  { key: 'digital_transformation', value: 'Digital Transformation' },
  { key: 'automation_solutions', value: 'Automation Solutions' },
  { key: 'performance_metrics', value: 'Performance Metrics' },
  { key: 'cost_reduction_strategies', value: 'Cost Reduction Strategies' },
  { key: 'quality_control', value: 'Quality Control' },
  { key: 'workflow_improvement', value: 'Workflow Improvement' },
  { key: 'remote_work_solutions', value: 'Remote Work Solutions' },
  { key: 'vendor_management', value: 'Vendor Management' },
  { key: 'supply_chain_efficiency', value: 'Supply Chain Efficiency' },
  { key: 'emerging_technology_trends', value: 'Emerging Technology Trends' },
  { key: 'software_integration', value: 'Software Integration' },
  { key: 'cybersecurity_best_practices', value: 'Cybersecurity Best Practices' },
  { key: 'data_analytics', value: 'Data Analytics' },
  { key: 'cloud_solutions', value: 'Cloud Solutions' },
  { key: 'mobile_optimization', value: 'Mobile Optimization' },
  { key: 'system_updates', value: 'System Updates' },
  { key: 'security_protocols', value: 'Security Protocols' },
  { key: 'technology_stack', value: 'Technology Stack' },
  { key: 'innovation_strategies', value: 'Innovation Strategies' },
  { key: 'lead_generation', value: 'Lead Generation' },
  { key: 'customer_acquisition', value: 'Customer Acquisition' },
  { key: 'brand_positioning', value: 'Brand Positioning' },
  { key: 'content_marketing', value: 'Content Marketing' },
  { key: 'seo_strategies', value: 'SEO Strategies' },
  { key: 'social_media_marketing', value: 'Social Media Marketing' },
  { key: 'email_marketing', value: 'Email Marketing' },
  { key: 'conversion_optimization', value: 'Conversion Optimization' },
  { key: 'customer_retention', value: 'Customer Retention' },
  { key: 'marketing_automation', value: 'Marketing Automation' },
  { key: 'budget_planning', value: 'Budget Planning' },
  { key: 'revenue_growth', value: 'Revenue Growth' },
  { key: 'cash_flow_management', value: 'Cash Flow Management' },
  { key: 'investment_strategies', value: 'Investment Strategies' },
  { key: 'cost_analysis', value: 'Cost Analysis' },
  { key: 'roi_optimization', value: 'ROI Optimization' },
  { key: 'financial_planning', value: 'Financial Planning' },
  { key: 'funding_options', value: 'Funding Options' },
  { key: 'tax_strategies', value: 'Tax Strategies' },
  { key: 'financial_compliance', value: 'Financial Compliance' },
  { key: 'talent_acquisition', value: 'Talent Acquisition' },
  { key: 'employee_engagement', value: 'Employee Engagement' },
  { key: 'training_programs', value: 'Training Programs' },
  { key: 'performance_management', value: 'Performance Management' },
  { key: 'team_building', value: 'Team Building' },
  { key: 'leadership_development', value: 'Leadership Development' },
  { key: 'workplace_culture', value: 'Workplace Culture' },
  { key: 'benefits_optimization', value: 'Benefits Optimization' },
  { key: 'diversity_inclusion', value: 'Diversity & Inclusion' },
  { key: 'remote_team_management', value: 'Remote Team Management' },
  { key: 'customer_satisfaction', value: 'Customer Satisfaction' },
  { key: 'service_excellence', value: 'Service Excellence' },
  { key: 'client_relations', value: 'Client Relations' },
  { key: 'support_systems', value: 'Support Systems' },
  { key: 'user_experience', value: 'User Experience' },
  { key: 'feedback_management', value: 'Feedback Management' },
  { key: 'customer_success', value: 'Customer Success' },
  { key: 'retention_programs', value: 'Retention Programs' },
  { key: 'service_quality', value: 'Service Quality' },
  { key: 'customer_journey', value: 'Customer Journey' },
  { key: 'regulatory_updates', value: 'Regulatory Updates' },
  { key: 'risk_assessment', value: 'Risk Assessment' },
  { key: 'compliance_monitoring', value: 'Compliance Monitoring' },
  { key: 'data_privacy', value: 'Data Privacy' },
  { key: 'security_audits', value: 'Security Audits' },
  { key: 'legal_requirements', value: 'Legal Requirements' },
  { key: 'insurance_reviews', value: 'Insurance Reviews' },
  { key: 'contract_management', value: 'Contract Management' },
  { key: 'risk_mitigation', value: 'Risk Mitigation' },
  { key: 'regulatory_compliance', value: 'Regulatory Compliance' },
  { key: 'market_trends', value: 'Market Trends' },
  { key: 'industry_reports', value: 'Industry Reports' },
  { key: 'competitive_analysis', value: 'Competitive Analysis' },
  { key: 'best_practices', value: 'Best Practices' },
  { key: 'case_studies', value: 'Case Studies' },
  { key: 'research_findings', value: 'Research Findings' },
  { key: 'market_opportunities', value: 'Market Opportunities' },
  { key: 'industry_news', value: 'Industry News' },
  { key: 'expert_opinions', value: 'Expert Opinions' },
  { key: 'benchmarking', value: 'Benchmarking' },
  { key: 'skills_training', value: 'Skills Training' },
  { key: 'certification_programs', value: 'Certification Programs' },
  { key: 'career_growth', value: 'Career Growth' },
  { key: 'professional_networking', value: 'Professional Networking' },
  { key: 'industry_events', value: 'Industry Events' },
  { key: 'learning_opportunities', value: 'Learning Opportunities' },
  { key: 'mentorship', value: 'Mentorship' },
  { key: 'leadership_training', value: 'Leadership Training' },
  { key: 'continuing_education', value: 'Continuing Education' },
  { key: 'professional_resources', value: 'Professional Resources' },
  { key: 'business_strategy', value: 'Business Strategy' },
  { key: 'growth_planning', value: 'Growth Planning' },
  { key: 'market_expansion', value: 'Market Expansion' },
  { key: 'partnership_opportunities', value: 'Partnership Opportunities' },
  { key: 'strategic_initiatives', value: 'Strategic Initiatives' },
  { key: 'long_term_planning', value: 'Long-term Planning' },
  { key: 'goal_setting', value: 'Goal Setting' },
  { key: 'vision_development', value: 'Vision Development' },
  { key: 'strategic_analysis', value: 'Strategic Analysis' },
  { key: 'business_development', value: 'Business Development' },
  { key: 'year_end_planning', value: 'Year-end Planning' },
  { key: 'quarterly_reviews', value: 'Quarterly Reviews' },
  { key: 'holiday_preparations', value: 'Holiday Preparations' },
  { key: 'new_year_goals', value: 'New Year Goals' },
  { key: 'summer_strategies', value: 'Summer Strategies' },
  { key: 'industry_conferences', value: 'Industry Conferences' },
  { key: 'annual_reports', value: 'Annual Reports' },
  { key: 'budget_cycles', value: 'Budget Cycles' },
  { key: 'tax_season', value: 'Tax Season' },
  { key: 'planning_sessions', value: 'Planning Sessions' },
  { key: 'team_communication', value: 'Team Communication' },
  { key: 'project_management', value: 'Project Management' },
  { key: 'collaboration_tools', value: 'Collaboration Tools' },
  { key: 'meeting_efficiency', value: 'Meeting Efficiency' },
  { key: 'internal_communications', value: 'Internal Communications' },
  { key: 'cross_department_coordination', value: 'Cross-department Coordination' },
  { key: 'stakeholder_management', value: 'Stakeholder Management' },
  { key: 'communication_strategies', value: 'Communication Strategies' },
  { key: 'team_productivity', value: 'Team Productivity' },
  { key: 'collaboration_best_practices', value: 'Collaboration Best Practices' },
] as const;

/** Persisted `industry` string must be one of these (FE dropdown + BE `@IsIn`). */
export const TARGET_INDUSTRY_VALUES: string[] = TARGET_INDUSTRY_OPTIONS.map((o) => o.value);

/** Each persisted `topics[]` entry must be one of these (FE dropdown + BE `@IsIn`). */
export const SUBTOPIC_VALUES: string[] = SUBTOPIC_OPTIONS.map((o) => o.value);

// ── Partner access request ──────────────────────────────────────────────────
// Wire values for the partner-access form's two dropdowns (in-app settings card
// and the docs site). Stored as-is in `partnerIntegrationForm`; labels are for display.

export const PARTNER_INBOX_VOLUMES = ['500_1000', '1000_5000', '5000_20000', '20000_plus'] as const;
export type PartnerInboxVolume = (typeof PARTNER_INBOX_VOLUMES)[number];
export const PARTNER_INBOX_VOLUME_LABEL: Record<PartnerInboxVolume, string> = {
  '500_1000': '500-1000',
  '1000_5000': '1000-5000',
  '5000_20000': '5000-20,000',
  '20000_plus': '20,000+',
};

export const PARTNER_USE_CASES = ['agency', 'saas_platform', 'esp', 'other'] as const;
export type PartnerUseCase = (typeof PARTNER_USE_CASES)[number];
export const PARTNER_USE_CASE_LABEL: Record<PartnerUseCase, string> = {
  agency: 'Agency or outbound shop managing client inboxes',
  saas_platform: 'SaaS or platform embedding warm-up into our product',
  esp: 'ESP or email service provider',
  other: 'Other',
};
